// src/components/MuseumLoanExport.jsx
import React, { useState } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";

const MuseumLoanExport = ({ selectedArtworks, collectionData, userData }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Configuration du dossier de prêt
  const [loanConfig, setLoanConfig] = useState({
    exhibitionTitle: "",
    exhibitionTheme: "",
    curatorText: "",
    museumName: "",
    exhibitionDates: { start: "", end: "" },
    installationDate: "",
    restitutionDate: "",
    insuranceValue: 0,
    transportMethod: "specialized", // 'specialized', 'self', 'courier'
    creditText: "",
    photographyAllowed: true,
    photographyCredit: "",
  });

  const safeFileName = (s) =>
    String(s || "")
      .replace(/[/\\?%*:|"<>]/g, "-")
      .replace(/\s+/g, " ")
      .trim();

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString("fr-FR");
  };

  // Helper image => base64
  const fetchImageAsBase64 = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Générer le PDF du dossier prêt (2 pages par œuvre, pagination robuste)
  const generateLoanDossier = async () => {
    setLoading(true);

    try {
      const artworksSafe = Array.isArray(selectedArtworks) ? selectedArtworks : [];
      const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

      const margin = 20;
      const pageHeight = doc.internal.pageSize.getHeight
        ? doc.internal.pageSize.getHeight()
        : doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.getWidth
        ? doc.internal.pageSize.getWidth()
        : doc.internal.pageSize.width;

      let y = 20;

      // ✅ Ajoute une page et force la page active (jsPDF v3 safe)
      const newPage = (startY = 30) => {
        doc.addPage("a4", "p");
        doc.setPage(doc.getNumberOfPages());
        y = startY;
      };

      const ensureSpace = (needed = 10) => {
        if (y + needed > pageHeight - margin) newPage(30);
      };

      const addWrappedText = (text, x, maxWidth, lineHeight = 5) => {
        const lines = doc.splitTextToSize(String(text || ""), maxWidth);
        lines.forEach((ln) => {
          ensureSpace(lineHeight + 2);
          doc.text(ln, x, y);
          y += lineHeight;
        });
        return lines.length;
      };

      const sectionTitle = (label) => {
        ensureSpace(14);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(label, margin, y);
        y += 8;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
      };

      const drawHR = () => {
        ensureSpace(6);
        doc.setDrawColor(220);
        doc.line(margin, y, pageWidth - margin, y);
        y += 6;
        doc.setDrawColor(0);
      };

      const drawCheckbox = (label, checked = false, x = margin, size = 4) => {
        ensureSpace(8);
        doc.rect(x, y - 3, size, size);
        if (checked) {
          doc.setFontSize(10);
          doc.text("✓", x + 0.8, y, { baseline: "middle" });
        }
        doc.setFontSize(10);
        doc.text(label, x + size + 3, y);
        y += 7;
      };

      const totalValue = artworksSafe.reduce(
        (sum, a) => sum + (a?.estimated_value || a?.current_estimated_value || 0),
        0
      );

      // ======================
      // PAGE 1 : COUVERTURE
      // ======================
      doc.setPage(1);
      y = 20;

      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("PROPOSITION DE PRÊT", pageWidth / 2, 70, { align: "center" });

      doc.setFontSize(16);
      doc.setFont("helvetica", "normal");
      doc.text(loanConfig.exhibitionTitle || "[Nom de l'exposition]", pageWidth / 2, 90, { align: "center" });

      doc.setFontSize(14);
      doc.text(`Collection ${collectionData?.name || userData?.name || ""}`, pageWidth / 2, 105, {
        align: "center",
      });

      const startExpo = fmtDate(loanConfig.exhibitionDates.start);
      const endExpo = fmtDate(loanConfig.exhibitionDates.end);
      doc.text(startExpo && endExpo ? `${startExpo} - ${endExpo}` : "[Dates d'exposition]", pageWidth / 2, 120, {
        align: "center",
      });

      // Image couverture
      if (artworksSafe[0]?.image_url) {
        try {
          const imgData = await fetchImageAsBase64(artworksSafe[0].image_url);
          // image centrée
          doc.addImage(imgData, "JPEG", 55, 140, 100, 100);
        } catch (err) {
          console.error("Erreur chargement image couverture:", err);
        }
      } else {
        // placeholder
        doc.setDrawColor(200);
        doc.rect(55, 140, 100, 100);
        doc.setTextColor(140);
        doc.setFontSize(10);
        doc.text("Image couverture (placeholder)", pageWidth / 2, 190, { align: "center" });
        doc.setTextColor(0);
        doc.setDrawColor(0);
      }

      // ======================
      // PAGE 2 : PRÉSENTATION
      // ======================
      newPage(30);

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Présentation", margin, y);
      y += 12;

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      const presentationText =
        loanConfig.curatorText ||
        `Dans le cadre de votre exposition "${loanConfig.exhibitionTitle || "[Titre]"}", je souhaite vous proposer le prêt de ${
          artworksSafe.length
        } œuvre${artworksSafe.length > 1 ? "s" : ""} de ma collection qui s'inscrivent particulièrement bien dans votre propos curatorial sur ${
          loanConfig.exhibitionTheme || "[thème]"
        }.

Cette sélection permettrait d'enrichir votre parcours d'exposition et d'apporter un éclairage complémentaire sur les thématiques que vous abordez.`;

      addWrappedText(presentationText, margin, pageWidth - 2 * margin, 6);
      y += 6;

      ensureSpace(60);
      // Encadré infos clés
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, y, pageWidth - 2 * margin, 55, "F");

      let boxY = y + 10;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("INFORMATIONS CLÉS", margin + 5, boxY);
      boxY += 7;

      doc.setFont("helvetica", "normal");
      doc.text(`• Musée : ${loanConfig.museumName || "[Nom du musée]"}`, margin + 5, boxY);
      boxY += 6;
      doc.text(`• Nombre d'œuvres proposées : ${artworksSafe.length}`, margin + 5, boxY);
      boxY += 6;
      doc.text(`• Valeur totale assurance : ${totalValue.toLocaleString("fr-FR")} €`, margin + 5, boxY);
      boxY += 6;
      doc.text(
        `• Période d'exposition : ${startExpo && endExpo ? `${startExpo} - ${endExpo}` : "[Dates]"}`,
        margin + 5,
        boxY
      );
      boxY += 6;

      const install = fmtDate(loanConfig.installationDate) || "[Date]";
      const restit = fmtDate(loanConfig.restitutionDate) || "[Date]";
      doc.text(`• Installation : ${install} | Restitution : ${restit}`, margin + 5, boxY);

      y += 70;

      // ======================
      // PAGE 3 : OVERVIEW LISTE
      // ======================
      newPage(30);

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Œuvres proposées", margin, y);
      y += 10;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      if (artworksSafe.length === 0) {
        addWrappedText("Aucune œuvre sélectionnée (placeholder).", margin, pageWidth - 2 * margin, 5);
      } else {
        artworksSafe.forEach((a, idx) => {
          ensureSpace(8);
          const artist = a.artist || "Artiste inconnu";
          const title = a.title || "Sans titre";
          const year = a.year || "—";
          doc.text(`${idx + 1}. ${artist} – ${title}, ${year}`, margin, y);
          y += 6;
        });
      }

      // ======================
      // PAGE 4 : OVERVIEW VIGNETTES (si possible)
      // ======================
      newPage(30);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Œuvres proposées – Vignettes", margin, y);
      y += 10;

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");

      // Grille 3 colonnes x 3 lignes max par page
      const cellW = (pageWidth - 2 * margin - 2 * 6) / 3; // 2 gaps de 6
      const cellH = 55;
      const gap = 6;

      const thumbs = artworksSafe.slice(0, 9);
      if (thumbs.length === 0) {
        addWrappedText("Vignettes indisponibles (aucune œuvre).", margin, pageWidth - 2 * margin, 5);
      } else {
        for (let i = 0; i < thumbs.length; i++) {
          const row = Math.floor(i / 3);
          const col = i % 3;
          const x = margin + col * (cellW + gap);
          const top = y + row * (cellH + 18);

          // cadre
          doc.setDrawColor(220);
          doc.rect(x, top, cellW, cellH);

          // image si dispo
          if (thumbs[i]?.image_url) {
            try {
              const img = await fetchImageAsBase64(thumbs[i].image_url);
              doc.addImage(img, "JPEG", x + 2, top + 2, cellW - 4, cellH - 4);
            } catch (e) {
              doc.setTextColor(140);
              doc.text("Image", x + cellW / 2, top + cellH / 2, { align: "center" });
              doc.setTextColor(0);
            }
          } else {
            doc.setTextColor(140);
            doc.text("Image", x + cellW / 2, top + cellH / 2, { align: "center" });
            doc.setTextColor(0);
          }

          const artist = thumbs[i]?.artist || "Artiste";
          const title = thumbs[i]?.title || "Sans titre";
          const year = thumbs[i]?.year || "—";
          doc.setTextColor(0);
          doc.text(`${artist}`, x, top + cellH + 6, { maxWidth: cellW });
          doc.text(`${title} (${year})`, x, top + cellH + 11, { maxWidth: cellW });
        }
      }
      doc.setDrawColor(0);

      // ======================
      // PAGES : 2 pages par œuvre
      // ======================
      const perArtwork = artworksSafe.length ? artworksSafe : [
        {
          artist: "Artiste",
          title: "Sans titre",
          year: "—",
          medium: "Technique non spécifiée",
          dimensions: "Dimensions non spécifiées",
          condition: "Excellent/Bon/Moyen",
          acquisition_source: "",
          estimated_value: 0,
          current_estimated_value: 0,
          image_url: "",
        },
      ];

      for (let i = 0; i < perArtwork.length; i++) {
        const a = perArtwork[i];

        // ---- PAGE A : Photo + Infos condensées
        newPage(30);

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(`FICHE ŒUVRE ${i + 1}/${perArtwork.length} – VISUEL`, pageWidth / 2, y, { align: "center" });
        y += 10;

        // Bloc image grand
        const imgW = 150;
        const imgH = 150;
        const xImg = (pageWidth - imgW) / 2;

        if (a.image_url) {
          try {
            const img = await fetchImageAsBase64(a.image_url);
            doc.addImage(img, "JPEG", xImg, y, imgW, imgH);
          } catch (e) {
            doc.setDrawColor(200);
            doc.rect(xImg, y, imgW, imgH);
            doc.setTextColor(140);
            doc.setFontSize(10);
            doc.text("Image (placeholder)", pageWidth / 2, y + imgH / 2, { align: "center" });
            doc.setTextColor(0);
            doc.setDrawColor(0);
          }
        } else {
          doc.setDrawColor(200);
          doc.rect(xImg, y, imgW, imgH);
          doc.setTextColor(140);
          doc.setFontSize(10);
          doc.text("Image (placeholder)", pageWidth / 2, y + imgH / 2, { align: "center" });
          doc.setTextColor(0);
          doc.setDrawColor(0);
        }
        y += imgH + 10;

        // Infos condensées
        const artist = a.artist || "Artiste inconnu";
        const title = a.title || "Sans titre";
        const year = a.year || "—";
        const medium = a.medium || "Technique non spécifiée";
        const dims = a.dimensions || "Dimensions non spécifiées";

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(artist, margin, y);
        y += 7;

        doc.setFontSize(11);
        doc.setFont("helvetica", "italic");
        doc.text(title, margin, y);
        doc.setFont("helvetica", "normal");
        doc.text(String(year), pageWidth - margin, y, { align: "right" });
        y += 7;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(medium, margin, y);
        y += 5;
        doc.text(dims, margin, y);
        y += 8;

        drawHR();

        doc.setFont("helvetica", "bold");
        doc.text("Résumé / Notes", margin, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        addWrappedText("—", margin, pageWidth - 2 * margin, 5);

        // ---- PAGE B : Provenance + État + Valeur + Conditions
        newPage(30);

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(`FICHE ŒUVRE ${i + 1}/${perArtwork.length} – CONDITIONS`, pageWidth / 2, y, { align: "center" });
        y += 12;

        // Provenance
        sectionTitle("PROVENANCE SYNTHÉTIQUE");
        const prov = a.acquisition_source ? `Acquis auprès de ${a.acquisition_source}` : "Acquisition : [à compléter]";
        addWrappedText(`• ${prov}`, margin, pageWidth - 2 * margin, 5);
        addWrappedText("• Collection actuelle : Collection privée", margin, pageWidth - 2 * margin, 5);
        y += 4;

        // État
        sectionTitle("ÉTAT DE CONSERVATION");
        addWrappedText(a.condition || "Excellent/Bon/Moyen (à préciser)", margin, pageWidth - 2 * margin, 5);
        y += 4;

        // Valeur assurance
        sectionTitle("VALEUR ASSURANCE");
        const insValue = a.estimated_value || a.current_estimated_value || 0;
        addWrappedText(`${insValue.toLocaleString("fr-FR")} €`, margin, pageWidth - 2 * margin, 5);
        y += 4;

        // Conditions spécifiques
        sectionTitle("CONDITIONS DE PRÊT (référence)");
        addWrappedText("Se référer aux conditions générales de prêt (section dédiée).", margin, pageWidth - 2 * margin, 5);
      }

      // ======================
      // CONDITIONS GÉNÉRALES (1 page)
      // ======================
      newPage(30);

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Conditions générales de prêt", margin, y);
      y += 14;

      // Assurance
      sectionTitle("ASSURANCE");
      const insuranceTotal = perArtwork.reduce(
        (sum, a) => sum + (a?.estimated_value || a?.current_estimated_value || 0),
        0
      );
      addWrappedText(`• Valeur totale assurance : ${insuranceTotal.toLocaleString("fr-FR")} €`, margin, pageWidth - 2 * margin, 5);
      addWrappedText("• Couverture clou à clou exigée", margin, pageWidth - 2 * margin, 5);
      addWrappedText("• Franchises : à la charge de l'emprunteur", margin, pageWidth - 2 * margin, 5);
      y += 6;

      // Transport
      sectionTitle("TRANSPORT");
      const transportText =
        loanConfig.transportMethod === "specialized"
          ? "Transport spécialisé art requis"
          : loanConfig.transportMethod === "self"
          ? "Convoiement par moi-même/courrier"
          : "Selon modalités à définir";
      addWrappedText(`• ${transportText}`, margin, pageWidth - 2 * margin, 5);
      addWrappedText("• Emballage selon normes musées", margin, pageWidth - 2 * margin, 5);
      addWrappedText("• Constat d'état aller-retour obligatoire", margin, pageWidth - 2 * margin, 5);
      y += 6;

      // Conservation
      sectionTitle("INSTALLATION & CONSERVATION");
      [
        "• Température : 18-22°C, HR : 45-55%",
        "• Éclairage : Max 150 lux",
        "• Pas de lumière directe soleil",
        "• Vitrine/encadrement sécurisé pour œuvres fragiles",
      ].forEach((ln) => addWrappedText(ln, margin, pageWidth - 2 * margin, 5));
      y += 6;

      // Crédit
      sectionTitle("CRÉDIT");
      addWrappedText(
        loanConfig.creditText || `"Collection ${collectionData?.name || "[Nom]"}" ou "Prêt Collection [Nom]"`,
        margin,
        pageWidth - 2 * margin,
        5
      );
      y += 6;

      // Photos
      sectionTitle("PHOTOGRAPHIE");
      addWrappedText(
        `• Photos reproduction ${loanConfig.photographyAllowed ? "autorisées" : "non autorisées"} pour catalogue/communication`,
        margin,
        pageWidth - 2 * margin,
        5
      );
      addWrappedText(
        `• Crédit photographique : ${loanConfig.photographyCredit || "[si applicable]"}`,
        margin,
        pageWidth - 2 * margin,
        5
      );
      y += 6;

      // Durée
      sectionTitle("DURÉE DU PRÊT");
      addWrappedText(`• Installation : ${install}`, margin, pageWidth - 2 * margin, 5);
      addWrappedText(`• Exposition : ${startExpo || "[Date début]"} - ${endExpo || "[Date fin]"}`, margin, pageWidth - 2 * margin, 5);
      addWrappedText(`• Restitution : ${restit}`, margin, pageWidth - 2 * margin, 5);

      // ======================
      // FACILITY REPORT (1 page)
      // ======================
      newPage(30);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Facility report", margin, y);
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.text("(à compléter par le musée)", margin + 70, y);
      y += 14;

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("RAPPORT DE CONSTAT D'ÉTAT", margin, y);
      y += 10;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("À compléter par l'équipe de conservation du musée :", margin, y);
      y += 10;

      drawCheckbox("Départ collection", false);
      drawCheckbox("Arrivée musée", false);
      drawCheckbox("Retour collection", false);
      y += 6;

      doc.setFont("helvetica", "bold");
      doc.text("Schéma / zones à annoter :", margin, y);
      y += 6;

      // Zone schéma
      doc.setDrawColor(200);
      doc.rect(margin, y, pageWidth - 2 * margin, 70);
      doc.setTextColor(140);
      doc.setFontSize(10);
      doc.text("Zone d'annotation (placeholder)", pageWidth / 2, y + 35, { align: "center" });
      doc.setTextColor(0);
      doc.setDrawColor(0);
      y += 80;

      doc.setFont("helvetica", "bold");
      doc.text("Observations :", margin, y);
      y += 8;
      doc.setFont("helvetica", "normal");
      for (let i = 0; i < 5; i++) {
        ensureSpace(8);
        doc.text("_".repeat(90), margin, y);
        y += 7;
      }

      // ======================
      // CONTACT (1 page)
      // ======================
      newPage(30);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Contact", margin, y);
      y += 14;

      sectionTitle("INFORMATIONS DE CONTACT");
      addWrappedText(`${userData?.name || "[Vos coordonnées]"}`, margin, pageWidth - 2 * margin, 5);
      addWrappedText("Tél. : [...]", margin, pageWidth - 2 * margin, 5);
      addWrappedText("Email : [...]", margin, pageWidth - 2 * margin, 5);
      y += 10;

      doc.setFont("helvetica", "bold");
      doc.text("Disponibilité pour visite atelier/résidence :", margin, y);
      y += 7;
      doc.setFont("helvetica", "normal");
      addWrappedText("[Préciser vos disponibilités pour que le conservateur puisse voir les œuvres]", margin, pageWidth - 2 * margin, 5);

      // Save
      const fileName = `Dossier_Pret_${safeFileName(loanConfig.exhibitionTitle || "Exposition")}_${safeFileName(
        collectionData?.name || "Collection"
      )}_${new Date().toISOString().split("T")[0]}.pdf`;

      doc.save(fileName);

      setLoading(false);
      setShowModal(false);
    } catch (error) {
      console.error("Erreur génération PDF prêt:", error);
      alert("Erreur lors de la génération du dossier. Veuillez réessayer.");
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        disabled={!selectedArtworks || selectedArtworks.length === 0}
        style={{
          padding: "12px 24px",
          backgroundColor: selectedArtworks?.length > 0 ? "#000" : "#ccc",
          color: "#fff",
          border: "none",
          borderRadius: "2px",
          fontSize: "14px",
          fontFamily: "Georgia, serif",
          cursor: selectedArtworks?.length > 0 ? "pointer" : "not-allowed",
          transition: "all 0.2s ease",
        }}
      >
        Dossier prêt exposition ({selectedArtworks?.length || 0} œuvre{selectedArtworks?.length > 1 ? "s" : ""})
      </button>

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "40px",
              borderRadius: "2px",
              maxWidth: "700px",
              width: "90%",
              maxHeight: "85vh",
              overflowY: "auto",
              fontFamily: "Georgia, serif",
            }}
          >
            <h2 style={{ fontSize: "24px", fontWeight: "400", marginBottom: "30px", color: "#000" }}>
              Configuration dossier de prêt
            </h2>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#000",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Titre de l'exposition *
              </label>
              <input
                type="text"
                value={loanConfig.exhibitionTitle}
                onChange={(e) => setLoanConfig({ ...loanConfig, exhibitionTitle: e.target.value })}
                placeholder="Ex: Abstraction et Paysage"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "2px",
                  fontSize: "14px",
                  fontFamily: "Georgia, serif",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#000",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Thème de l'exposition
              </label>
              <input
                type="text"
                value={loanConfig.exhibitionTheme}
                onChange={(e) => setLoanConfig({ ...loanConfig, exhibitionTheme: e.target.value })}
                placeholder="Ex: L'art abstrait des années 50"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "2px",
                  fontSize: "14px",
                  fontFamily: "Georgia, serif",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#000",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Nom du musée
              </label>
              <input
                type="text"
                value={loanConfig.museumName}
                onChange={(e) => setLoanConfig({ ...loanConfig, museumName: e.target.value })}
                placeholder="Ex: Musée d'Art Moderne"
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "2px",
                  fontSize: "14px",
                  fontFamily: "Georgia, serif",
                }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#000",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Date début expo *
                </label>
                <input
                  type="date"
                  value={loanConfig.exhibitionDates.start}
                  onChange={(e) =>
                    setLoanConfig({
                      ...loanConfig,
                      exhibitionDates: { ...loanConfig.exhibitionDates, start: e.target.value },
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "2px",
                    fontSize: "14px",
                    fontFamily: "Georgia, serif",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#000",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Date fin expo *
                </label>
                <input
                  type="date"
                  value={loanConfig.exhibitionDates.end}
                  onChange={(e) =>
                    setLoanConfig({
                      ...loanConfig,
                      exhibitionDates: { ...loanConfig.exhibitionDates, end: e.target.value },
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "2px",
                    fontSize: "14px",
                    fontFamily: "Georgia, serif",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#000",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Date installation
                </label>
                <input
                  type="date"
                  value={loanConfig.installationDate}
                  onChange={(e) => setLoanConfig({ ...loanConfig, installationDate: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "2px",
                    fontSize: "14px",
                    fontFamily: "Georgia, serif",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#000",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Date restitution
                </label>
                <input
                  type="date"
                  value={loanConfig.restitutionDate}
                  onChange={(e) => setLoanConfig({ ...loanConfig, restitutionDate: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "2px",
                    fontSize: "14px",
                    fontFamily: "Georgia, serif",
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#000",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Mode de transport
              </label>
              <select
                value={loanConfig.transportMethod}
                onChange={(e) => setLoanConfig({ ...loanConfig, transportMethod: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "2px",
                  fontSize: "14px",
                  fontFamily: "Georgia, serif",
                  backgroundColor: "#fff",
                }}
              >
                <option value="specialized">Transport spécialisé art requis</option>
                <option value="self">Convoiement par moi-même/courrier</option>
                <option value="other">Selon modalités à définir</option>
              </select>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#000",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Texte de présentation personnalisé
              </label>
              <textarea
                value={loanConfig.curatorText}
                onChange={(e) => setLoanConfig({ ...loanConfig, curatorText: e.target.value })}
                placeholder="Texte expliquant pourquoi ces œuvres correspondent à l'exposition..."
                rows={4}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "2px",
                  fontSize: "14px",
                  fontFamily: "Georgia, serif",
                  resize: "vertical",
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", fontSize: "14px", cursor: "pointer", marginBottom: "10px" }}>
                <input
                  type="checkbox"
                  checked={loanConfig.photographyAllowed}
                  onChange={(e) => setLoanConfig({ ...loanConfig, photographyAllowed: e.target.checked })}
                  style={{ marginRight: "10px" }}
                />
                Photos reproduction autorisées pour catalogue
              </label>

              {loanConfig.photographyAllowed && (
                <input
                  type="text"
                  value={loanConfig.photographyCredit}
                  onChange={(e) => setLoanConfig({ ...loanConfig, photographyCredit: e.target.value })}
                  placeholder="Crédit photographique (si applicable)"
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "2px",
                    fontSize: "14px",
                    fontFamily: "Georgia, serif",
                  }}
                />
              )}
            </div>

            <div style={{ display: "flex", gap: "15px", marginTop: "30px", paddingTop: "20px", borderTop: "1px solid #eee" }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: "#f5f5f5",
                  color: "#000",
                  border: "1px solid #ddd",
                  borderRadius: "2px",
                  fontSize: "14px",
                  fontFamily: "Georgia, serif",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                Annuler
              </button>
              <button
                onClick={generateLoanDossier}
                disabled={loading || !loanConfig.exhibitionTitle || !loanConfig.exhibitionDates.start || !loanConfig.exhibitionDates.end}
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor:
                    loading || !loanConfig.exhibitionTitle || !loanConfig.exhibitionDates.start || !loanConfig.exhibitionDates.end ? "#999" : "#000",
                  color: "#fff",
                  border: "none",
                  borderRadius: "2px",
                  fontSize: "14px",
                  fontFamily: "Georgia, serif",
                  cursor:
                    loading || !loanConfig.exhibitionTitle || !loanConfig.exhibitionDates.start || !loanConfig.exhibitionDates.end
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {loading ? "Génération..." : "Générer le dossier PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MuseumLoanExport;
